// src/utils/lastSeries.js
//
// Lembra, por usuário e por texto, a última "série de anotações" (Activity)
// aberta na tela de anotação. É a fonte primária da decisão "abrir a última
// série usada" — o carimbo activities.last_opened_at do backend é só reforço.
//
// Escopo local ao dispositivo (localStorage), key litterae:lastSeries:{userId}.
// Todas as operações são defensivas: navegador sem storage, cota estourada ou
// JSON corrompido nunca devem quebrar o fluxo de anotar.

import { decodeManagerUserId } from "@/utils/permissionDefaults";

const KEY_PREFIX = "litterae:lastSeries:";

// A memória é por usuário E POR CONTEXTO DE LOGIN. Uma pessoa que é membro de
// uma escola e também titular da própria conta trabalha em séries diferentes em
// cada contexto; com a chave só por userId, a série lembrada num contexto
// reabria no outro — inclusive séries que o contexto atual não pode usar.
function keyFor(userId) {
  const managerId = decodeManagerUserId();
  const scope = managerId != null ? `m${managerId}` : "own";
  return `${KEY_PREFIX}${userId ?? "anon"}:${scope}`;
}

function readMap(userId) {
  try {
    const raw = window.localStorage.getItem(keyFor(userId));
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === "object" ? parsed : {};
  } catch {
    return {};
  }
}

function writeMap(userId, map) {
  try {
    window.localStorage.setItem(keyFor(userId), JSON.stringify(map));
  } catch {
    /* storage indisponível ou cheio — segue sem lembrar */
  }
}

/** activityId lembrado para (userId, textId), ou null. */
export function readLastSeries(userId, textId) {
  if (!textId) return null;
  const map = readMap(userId);
  const value = Number(map[String(textId)]);
  return Number.isFinite(value) && value > 0 ? value : null;
}

/** Grava a série usada para (userId, textId). */
export function writeLastSeries(userId, textId, activityId) {
  if (!textId || !activityId) return;
  const map = readMap(userId);
  map[String(textId)] = Number(activityId);
  writeMap(userId, map);
}

/** Esquece a série de um texto (ex.: ela foi apagada). */
export function forgetLastSeries(userId, textId) {
  if (!textId) return;
  const map = readMap(userId);
  if (String(textId) in map) {
    delete map[String(textId)];
    writeMap(userId, map);
  }
}
